import { currentCabinetRole } from '../lib/domains';
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { traderAuthAPI, merchantAuthAPI, UserResponse, LoginRequest, RegisterRequest, getTraderAccessToken } from '../lib/api';
import { identifyAnalyticsUser, resetAnalyticsUser } from '../lib/analytics';

interface AuthContextType {
    user: UserResponse | null;
    userType: 'trader' | 'merchant' | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    login: (credentials: LoginRequest) => Promise<void>;
    register: (userData: RegisterRequest) => Promise<void>;
    logout: () => void;
    refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth должен использоваться внутри AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<UserResponse | null>(null);
    const [userType, setUserType] = useState<'trader' | 'merchant' | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const isAuthenticated = !!user;

    // Проверка токена при загрузке приложения
    useEffect(() => {
        if (currentCabinetRole() === 'merchant') {
            setIsLoading(false);
            return;
        }
        const initAuth = async () => {
            const traderToken = getTraderAccessToken();
            const merchantToken = localStorage.getItem('merchant_access_token');

            if (traderToken) {
                try {
                    const userData = await traderAuthAPI.getCurrentUser();
                    if (userData) {
                        setUser(userData);
                        setUserType('trader');
                        identifyAnalyticsUser('trader', userData);
                    }
                } catch (error) {
                    localStorage.removeItem('trader_access_token');
                }
            } else if (merchantToken) {
                try {
                    const userData = await merchantAuthAPI.getCurrentUser();
                    if (userData) {
                        setUser(userData);
                        setUserType('merchant');
                        identifyAnalyticsUser('merchant', userData);
                    }
                } catch (error) {
                    // Токен недействителен, удаляем его
                    localStorage.removeItem('merchant_access_token');
                }
            }
            setIsLoading(false);
        };

        initAuth();
    }, []);

    const login = async (credentials: LoginRequest) => {
        try {
            setIsLoading(true);
            const response = await traderAuthAPI.login(credentials);

            // Сохраняем токен
            localStorage.setItem('trader_access_token', response.access_token);

            // Получаем данные пользователя
            const userData = await traderAuthAPI.getCurrentUser();
            setUser(userData);
            setUserType('trader');
            identifyAnalyticsUser('trader', userData);
        } catch (error) {
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const register = async (userData: RegisterRequest) => {
        try {
            setIsLoading(true);
            const newUser = await traderAuthAPI.register(userData);

            // После успешной регистрации автоматически авторизуем пользователя
            await login({
                username: userData.username,
                password: userData.password,
            });
        } catch (error) {
            setIsLoading(false);
            throw error;
        }
    };

    const logout = () => {
        traderAuthAPI.logout();
        setUser(null);
        setUserType(null);
        resetAnalyticsUser();
    };

    const refreshUser = async () => {
        try {
            const token = getTraderAccessToken();

            console.log('refreshUser:', { hasToken: !!token });

            if (token) {
                const userData = await traderAuthAPI.getCurrentUser();
                console.log('Трейдер загружен:', userData);

                if (userData) {
                    setUser(userData);
                    setUserType('trader');
                    identifyAnalyticsUser('trader', userData);
                    console.log('Пользователь установлен в контекст');
                }
            }
        } catch (error) {
            console.error('Ошибка refreshUser:', error);
            logout();
        }
    };

    const value: AuthContextType = {
        user,
        userType,
        isLoading,
        isAuthenticated,
        login,
        register,
        logout,
        refreshUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
